import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";

import { store, useAppSelector } from '@/app/store';
import { LanguageInfo, copyLanguageInfo, copyLanguagesHolder, getNewLanguageInfo } from '@/features/languages/languagesSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import { NumberText } from '@/components/Common/Field';
import MenuItem from '@/components/Common/MenuItem';
import { DeleteModal, RightBar } from '@/components/Common/Modals';

import Language from './Language';
import { RoleContext } from '@/app/api/common';
import { usePageSubscription } from '@/app/websocket/hooks';

const Languages = () => {
  const { t } = useTranslation();

  usePageSubscription([{ type: 'language' }]);

  const languages = useAppSelector((state) => state.languages);
  const navigate = useNavigate();
  const { lang_code } = useParams();
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as LanguageInfo[]);
  const [deleteNum, setDeleteNum] = React.useState(-1);
  const role = React.useContext(RoleContext);

  const langNum = React.useMemo(() => {
    if (!lang_code) return -1;
    if (lang_code === '__new__') return -2;
    const idx = languages?.languages?.findIndex((l: any) => l.code === lang_code);
    return idx !== undefined && idx >= 0 ? idx : -1;
  }, [lang_code, languages.languages]);

  React.useEffect(() => {
    store.getLanguages();
  }, []);

  React.useEffect(() => {
    setCols([
    {
        field:			"name",
        headerName:		t("languages:language_name"),
        flex:			1,
        minWidth:		200,
    },
    {
        field:			"code",
        headerName:		t("languages:language_code"),
        flex:			1,
        minWidth:		150,
    },
    {
        field:			"priority",
        headerName:		t("languages:priority"),
        flex:			0,
        minWidth:		120,
        maxWidth:		120,
    },
    ...(role?.admin || role?.privilages?.includes("ChangeLanguages") || role?.privilages?.includes("DeleteLanguages") ? [{
        field:			"options",
        headerName:		t("languages:options"),
        flex:			0,
        filter:			false,
        minWidth:		90,
        maxWidth:		90,
        cellRenderer:	OptionsCell,
    }] : []),
    ]);

    const rws: LanguageInfo[] = [];
    for (let i = 0; i < languages?.languages?.length; i++) {
      let lang = copyLanguageInfo(languages?.languages[i]);
      lang.name = lang.name.charAt(0).toUpperCase() + lang.name.slice(1);
      let langAny = lang as any;

      langAny.requestDelete = () => setDeleteNum(i);
      langAny.requestEdit = () => navigate('/settings/languages/' + encodeURIComponent(lang.code));
      langAny.__index__ = i;

      rws.push(lang);
    }

    setRows(rws);
  }, [languages.languages, t]);

  const updateIsOpen = (val: boolean) => {
    if (!val)
      navigate('/settings/languages');
  };

  const processDelete = React.useCallback(async (_obj: Object, answer: string) => {
    setDeleteNum(-1);

    if (answer !== "yes") {
      return;
    }

    if (deleteNum < -1)
      return;

    const nlangs = copyLanguagesHolder(languages);
    if (	!nlangs ||
        	nlangs?.languages?.length <= deleteNum
       )
      return;

    nlangs.languages.splice(deleteNum, 1);

    const mess = await store.setLanguages(nlangs);
    console.log("Languages/processDelete/deleteNum=" + deleteNum + "/mess=", mess);
  }, [languages.languages, deleteNum]);

  const updateLanguage = React.useCallback(async (n: number, lang: LanguageInfo) => {
    const nlangs = copyLanguagesHolder(languages);
    if (	!lang ||
    		n == -1 ||
    		!nlangs ||
        	nlangs?.languages?.length <= n
       )
      return;

    const nlang = copyLanguageInfo(lang);
    if (n == -2) {
      nlangs?.languages.push(nlang);
    } else {
      nlangs.languages[n] = nlang;
    }

    const mess = await store.setLanguages(nlangs);
    console.log("Languages/updateLanguage/n=" + n + "/mess=", mess);
  }, [languages.languages]);

  const handleCellClick = React.useCallback((evt: any) => {
    if (evt.colDef.field == "options")
      return;
    if(!role?.admin && !role?.privilages?.includes("ViewLanguages"))
      return;
    navigate('/settings/languages/' + encodeURIComponent(evt.data.code));
  }, [navigate]);

  const getLangDesc = React.useCallback((n: number): LanguageInfo | null => {
    if (n == -2)
      return getNewLanguageInfo();

    if (	n < 0 ||
    		n >= rows?.length
       )
      return null;

    return rows[n];
  }, [rows]);

  return (
  <React.Fragment>
    <FlexRow direction="column"
	         align="start"
	         className={[
               'bg-white',
               'px-[32px] pt-[32px] sticky top-[64px] z-10',
             ]}>
      <div className="text-[32px]/[48px]">
        { t('languages:languages') }
      </div>
      <div>
        <NumberText>
          { rows?.length }
        </NumberText>
        { ' ' +
          t('languages:new_languages')
        }
      </div>
      <Divider className="my-[8px]" />
      {(role?.admin || role?.privilages?.includes("ChangeLanguages")) &&
        <Button caption={t('languages:new_languages')}
                icon="add"
                className="uppercase mt-[20px]"
                onClick={() => navigate('/settings/languages/__new__')} />
      }
    </FlexRow>
    <FlexRow direction="column"
             align="start"
             className={[
               "py-[16px]",
               "px-[32px]"
             ]}>

      <Grid rowData={rows}
            columnDefs={cols}
            filter={false}
            globalFilter={true}
            showPages={true}
            maxHeight="calc(100vh - 380px)"
            onCellClicked={handleCellClick}
            pageSize={10} />
    </FlexRow>
          {langNum != -1 &&
              <RightBar onOpenChange={updateIsOpen}>
                  <Language language={getLangDesc(langNum)}
                      index={langNum}
                      setLanguage={(lang: LanguageInfo) => updateLanguage(langNum, lang)}
                      onClose={() => navigate('/settings/languages')} />
              </RightBar>
          }
  {deleteNum > -1 && deleteNum < rows.length &&
	<DeleteModal title={t('languages:delete_language_:caption')}
	             message={t('languages:delete_language_:message_n', {n: rows[deleteNum]?.name})}
	             onResult={processDelete}>
      <>
      </>
    </DeleteModal>
  }
  </React.Fragment>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const role = React.useContext(RoleContext);
  const handleEdit = React.useCallback(() => {
    setOpen(false);
    props.data.requestEdit();
  }, [setOpen, props.data]);

  const handleDelete = React.useCallback(async () => {
    setOpen(false);
    props.data.requestDelete();
  }, [setOpen, props.data]);
  return (
  <Popover key={1}
           isOpen={open}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}
           onOpenChange={setOpen}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        {(role?.admin || role?.privilages?.includes("ChangeLanguages")) &&
          <MenuItem icon="edit_note"
                    label={t('languages:edit_language_information:caption')}
                    onClick={handleEdit} />
        }
        {(role?.admin || role?.privilages?.includes("DeleteLanguages")) &&
          <MenuItem icon="delete"
                    color="!text-[--Warning]"
                    label={t('languages:delete_language')}
                    onClick={handleDelete} />
        }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export default Languages;
